import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Package, Truck, Calendar, ArrowLeft, X, Save,
  Loader2, Box, Armchair, TentTree, Coffee, Lamp, Search
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUB_CATEGORIES = [
  { key: 'tables', label: 'Tables', icon: Box },
  { key: 'chairs', label: 'Chairs', icon: Armchair },
  { key: 'tents', label: 'Tents & Canopies', icon: TentTree },
  { key: 'linens', label: 'Linens & Covers', icon: Package },
  { key: 'equipment', label: 'Equipment', icon: Coffee },
  { key: 'lighting', label: 'Lighting', icon: Lamp },
  { key: 'decor', label: 'Decor', icon: Package },
  { key: 'general', label: 'General', icon: Package },
];

const EMPTY_FORM = {
  name: '', description: '', sub_category: 'tables', city: 'denver', state: 'CO',
  image_url: '', price_per_unit: '', quantity_on_hand: '',
  provider_id: 'prov_party_rentals_express', provider_name: 'Party Rentals Express',
  variants: [],
  delivery: { free_radius_miles: 10, per_mile_rate: 2.50, max_radius_miles: 50, min_rental_amount: 0 },
  damage_deposit: { enabled: false, type: 'flat', amount: 0 },
};

export default function RentalManager() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('inventory');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [filterCity, setFilterCity] = useState('');
  const [filterCat, setFilterCat] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    fetchData();
  }, []);

  const fetchData = async () => {
    const [itemsRes, summaryRes, bookingsRes] = await Promise.all([
      axios.get(`${API}/rentals/items`, { headers }),
      axios.get(`${API}/rentals/inventory-summary`, { headers }),
      axios.get(`${API}/rentals/bookings`, { headers }),
    ]);
    setItems(itemsRes.data.items || []);
    setSummary(summaryRes.data);
    setBookings(bookingsRes.data.bookings || []);
  };

  const saveItem = async () => {
    if (!form.name || !form.price_per_unit || !form.quantity_on_hand) {
      toast.error('Name, price, and quantity required'); return;
    }
    try {
      const data = { ...form, price_per_unit: parseFloat(form.price_per_unit), quantity_on_hand: parseInt(form.quantity_on_hand) };
      if (editingId) {
        await axios.put(`${API}/rentals/items/${editingId}`, data, { headers });
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/rentals/items`, data, { headers });
        toast.success('Item created');
      }
      setShowForm(false); setEditingId(null); setForm({ ...EMPTY_FORM });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteItem = async (id) => {
    if (!confirm('Delete this rental item?')) return;
    await axios.delete(`${API}/rentals/items/${id}`, { headers });
    toast.success('Deleted'); fetchData();
  };

  const editItem = (item) => {
    setForm({
      name: item.name, description: item.description || '', sub_category: item.sub_category || 'general',
      city: item.city || 'denver', state: item.state || '', image_url: item.image_url || '',
      price_per_unit: item.price_per_unit, quantity_on_hand: item.quantity_on_hand,
      provider_id: item.provider_id || '', provider_name: item.provider_name || '',
      variants: item.variants || [],
      delivery: item.delivery || { ...EMPTY_FORM.delivery },
      damage_deposit: item.damage_deposit || { ...EMPTY_FORM.damage_deposit },
    });
    setEditingId(item.item_id);
    setShowForm(true);
  };

  const addVariant = () => {
    setForm(p => ({ ...p, variants: [...p.variants, { variant_id: `var_${Date.now().toString(36)}`, name: '', price_modifier: 0, description: '' }] }));
  };

  const updateVariant = (idx, field, value) => {
    setForm(p => ({ ...p, variants: p.variants.map((v, i) => i === idx ? { ...v, [field]: value } : v) }));
  };

  const removeVariant = (idx) => {
    setForm(p => ({ ...p, variants: p.variants.filter((_, i) => i !== idx) }));
  };

  const markReturned = async (bookingId) => {
    await axios.post(`${API}/rentals/bookings/${bookingId}/returned`, {}, { headers });
    toast.success('Marked as returned'); fetchData();
  };

  const cancelBooking = async (bookingId) => {
    await axios.post(`${API}/rentals/bookings/${bookingId}/cancel`, {}, { headers });
    toast.success('Booking cancelled'); fetchData();
  };

  const filtered = items.filter(i => (!filterCity || i.city === filterCity) && (!filterCat || i.sub_category === filterCat));

  return (
    <div className="min-h-screen bg-background pt-16 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 mb-6 pt-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')}><ArrowLeft className="h-5 w-5" /></Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Rental Inventory Manager</h1>
            <p className="text-sm text-muted-foreground">Manage event rental stock, availability, and bookings</p>
          </div>
          <Button onClick={() => { setShowForm(true); setEditingId(null); setForm({ ...EMPTY_FORM }); }} data-testid="new-rental-btn">
            <Plus className="h-4 w-4 mr-1" /> New Rental Item
          </Button>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{summary.total_items}</p><p className="text-xs text-muted-foreground">Rental Items</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{summary.total_units}</p><p className="text-xs text-muted-foreground">Total Units</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-500">${summary.total_rental_value}</p><p className="text-xs text-muted-foreground">Inventory Value</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-indigo-500">{summary.active_bookings}</p><p className="text-xs text-muted-foreground">Active Bookings</p></CardContent></Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full mb-6">
            <TabsTrigger value="inventory" className="flex-1"><Package className="h-4 w-4 mr-2" /> Inventory</TabsTrigger>
            <TabsTrigger value="bookings" className="flex-1"><Calendar className="h-4 w-4 mr-2" /> Bookings ({bookings.length})</TabsTrigger>
          </TabsList>

          {/* INVENTORY TAB */}
          <TabsContent value="inventory">
            {/* Create/Edit Form */}
            {showForm && (
              <Card className="mb-6 border-indigo-500/20">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{editingId ? 'Edit Rental Item' : 'New Rental Item'}</h3>
                    <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}><X className="h-4 w-4" /></Button>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Item Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="6-Foot Rectangular Table" data-testid="rental-name" /></div>
                    <div className="space-y-1"><Label className="text-xs">Category</Label>
                      <select value={form.sub_category} onChange={e => setForm(p => ({ ...p, sub_category: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                        {SUB_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">City</Label>
                      <select value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                        <option value="denver">Denver, CO</option><option value="las_vegas">Las Vegas, NV</option><option value="los_angeles">Los Angeles, CA</option><option value="charlotte">Charlotte, NC</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Price per Unit ($) *</Label><Input type="number" step="0.01" value={form.price_per_unit} onChange={e => setForm(p => ({ ...p, price_per_unit: e.target.value }))} placeholder="20.00" data-testid="rental-price" /></div>
                    <div className="space-y-1"><Label className="text-xs">Quantity on Hand *</Label><Input type="number" value={form.quantity_on_hand} onChange={e => setForm(p => ({ ...p, quantity_on_hand: e.target.value }))} placeholder="8" data-testid="rental-qty" /></div>
                    <div className="space-y-1"><Label className="text-xs">Image URL</Label><Input value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} placeholder="https://..." /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} className="text-sm" /></div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Provider ID</Label><Input value={form.provider_id} onChange={e => setForm(p => ({ ...p, provider_id: e.target.value }))} /></div>
                    <div className="space-y-1"><Label className="text-xs">Provider Name</Label><Input value={form.provider_name} onChange={e => setForm(p => ({ ...p, provider_name: e.target.value }))} /></div>
                  </div>

                  {/* Variants */}
                  <div className="border-t border-border pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs font-semibold">Variants (optional)</Label>
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={addVariant}><Plus className="h-3 w-3 mr-1" /> Add Variant</Button>
                    </div>
                    {form.variants.map((v, i) => (
                      <div key={i} className="flex gap-2 mb-2">
                        <Input value={v.name} onChange={e => updateVariant(i, 'name', e.target.value)} placeholder="Spandex Cover - White" className="text-xs flex-1" />
                        <Input type="number" step="0.01" value={v.price_modifier} onChange={e => updateVariant(i, 'price_modifier', parseFloat(e.target.value) || 0)} placeholder="+$" className="text-xs w-20" />
                        <Button size="sm" variant="ghost" className="text-destructive h-8 w-8 p-0" onClick={() => removeVariant(i)}><X className="h-3 w-3" /></Button>
                      </div>
                    ))}
                  </div>

                  {/* Delivery Config */}
                  <div className="border-t border-border pt-3">
                    <Label className="text-xs font-semibold">Delivery Settings</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                      <div className="space-y-1"><Label className="text-[10px]">Free Radius (mi)</Label><Input type="number" value={form.delivery.free_radius_miles} onChange={e => setForm(p => ({ ...p, delivery: { ...p.delivery, free_radius_miles: parseFloat(e.target.value) || 0 } }))} className="h-8 text-xs" /></div>
                      <div className="space-y-1"><Label className="text-[10px]">$/Mile After</Label><Input type="number" step="0.01" value={form.delivery.per_mile_rate} onChange={e => setForm(p => ({ ...p, delivery: { ...p.delivery, per_mile_rate: parseFloat(e.target.value) || 0 } }))} className="h-8 text-xs" /></div>
                      <div className="space-y-1"><Label className="text-[10px]">Max Radius (mi)</Label><Input type="number" value={form.delivery.max_radius_miles} onChange={e => setForm(p => ({ ...p, delivery: { ...p.delivery, max_radius_miles: parseFloat(e.target.value) || 0 } }))} className="h-8 text-xs" /></div>
                      <div className="space-y-1"><Label className="text-[10px]">Min Rental ($)</Label><Input type="number" value={form.delivery.min_rental_amount} onChange={e => setForm(p => ({ ...p, delivery: { ...p.delivery, min_rental_amount: parseFloat(e.target.value) || 0 } }))} className="h-8 text-xs" /></div>
                    </div>
                  </div>

                  {/* Deposit Config */}
                  <div className="border-t border-border pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs font-semibold">Damage Deposit</Label>
                      <Switch checked={form.damage_deposit.enabled} onCheckedChange={c => setForm(p => ({ ...p, damage_deposit: { ...p.damage_deposit, enabled: c } }))} />
                    </div>
                    {form.damage_deposit.enabled && (
                      <div className="grid grid-cols-2 gap-2">
                        <select value={form.damage_deposit.type} onChange={e => setForm(p => ({ ...p, damage_deposit: { ...p.damage_deposit, type: e.target.value } }))} className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs">
                          <option value="flat">Flat ($)</option><option value="percent">Percent (%)</option>
                        </select>
                        <Input type="number" value={form.damage_deposit.amount} onChange={e => setForm(p => ({ ...p, damage_deposit: { ...p.damage_deposit, amount: parseFloat(e.target.value) || 0 } }))} className="h-8 text-xs" placeholder="Amount" />
                      </div>
                    )}
                  </div>

                  <Button onClick={saveItem} data-testid="save-rental"><Save className="h-4 w-4 mr-1" /> {editingId ? 'Update Item' : 'Create Item'}</Button>
                </CardContent>
              </Card>
            )}

            {/* Filters */}
            <div className="flex gap-2 flex-wrap mb-4">
              <select value={filterCity} onChange={e => setFilterCity(e.target.value)} className="flex h-9 rounded-md border border-input bg-background px-3 text-sm">
                <option value="">All Cities</option><option value="denver">Denver</option><option value="las_vegas">Las Vegas</option><option value="los_angeles">Los Angeles</option><option value="charlotte">Charlotte</option>
              </select>
              <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="flex h-9 rounded-md border border-input bg-background px-3 text-sm">
                <option value="">All Categories</option>{SUB_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <Badge variant="outline" className="self-center text-xs">{filtered.length} items</Badge>
            </div>

            {/* Items Grid */}
            <div className="space-y-2" data-testid="rental-list">
              {filtered.map(item => (
                <Card key={item.item_id} className="border-border">
                  <CardContent className="p-4 flex items-center gap-4">
                    {item.image_url && <img src={item.image_url} alt={item.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm">{item.name}</h3>
                        <Badge variant="outline" className="text-[9px] capitalize">{item.sub_category}</Badge>
                        <Badge variant="outline" className="text-[9px]">{item.city === 'las_vegas' ? 'Las Vegas' : item.city === 'los_angeles' ? 'Los Angeles' : item.city.charAt(0).toUpperCase() + item.city.slice(1)}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm font-bold text-emerald-500">${item.price_per_unit}/ea</span>
                        <span className="text-xs text-muted-foreground">{item.quantity_on_hand} on hand</span>
                        {item.variants?.length > 0 && <Badge className="bg-purple-500/20 text-purple-400 text-[9px]">{item.variants.length} variants</Badge>}
                        <span className="text-[10px] text-muted-foreground">{item.provider_name}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => editItem(item)}><Edit className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteItem(item.item_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* BOOKINGS TAB */}
          <TabsContent value="bookings">
            <div className="space-y-2" data-testid="bookings-list">
              {bookings.length === 0 ? (
                <Card className="text-center py-12"><CardContent><Calendar className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" /><p className="text-sm text-muted-foreground">No bookings yet</p></CardContent></Card>
              ) : bookings.map(b => (
                <Card key={b.booking_id} className="border-border">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">{b.item_name} x{b.quantity_booked}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">{b.event_date}</span>
                        <Badge className={b.status === 'confirmed' ? 'bg-emerald-500' : b.status === 'held' ? 'bg-amber-500' : b.status === 'returned' ? 'bg-blue-500' : 'bg-slate-400'}>{b.status}</Badge>
                        <span className="text-xs font-semibold text-emerald-500">${b.total_price}</span>
                      </div>
                    </div>
                    {b.status === 'confirmed' && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => markReturned(b.booking_id)}>Mark Returned</Button>
                        <Button size="sm" variant="outline" className="text-xs text-destructive" onClick={() => cancelBooking(b.booking_id)}>Cancel</Button>
                      </div>
                    )}
                    {b.status === 'held' && (
                      <Button size="sm" variant="outline" className="text-xs text-destructive" onClick={() => cancelBooking(b.booking_id)}>Release Hold</Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
